import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Share2, Link2, ImageDown, Lock, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { readCaption } from "@/lib/useCaption";

const NETWORKS = [
  ["facebook", "Facebook", (u) => `https://www.facebook.com/sharer/sharer.php?u=${u}`],
  ["x", "X (Twitter)", (u, t) => `https://twitter.com/intent/tweet?url=${u}&text=${t}`],
  ["whatsapp", "WhatsApp", (u, t) => `https://wa.me/?text=${t}%20${u}`],
  ["reddit", "Reddit", (u, t) => `https://www.reddit.com/submit?url=${u}&title=${t}`],
];

export const svgToImage = (svg) => new Promise((resolve, reject) => {
  const im = new Image();
  im.onload = () => resolve(im);
  im.onerror = reject;
  im.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(svg))}`;
});

async function buildImage(tab, svgs, caption) {
  const S = 1080;
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S;
  const cx = c.getContext("2d");
  cx.fillStyle = "#0a0d14";
  cx.fillRect(0, 0, S, S);
  cx.fillStyle = "#00f0ff";
  cx.fillRect(0, 0, S, 10);
  cx.textBaseline = "alphabetic";
  cx.fillStyle = "#ffffff";
  cx.font = "800 40px 'Barlow Condensed', sans-serif";
  cx.fillText("BEAT", 60, 80);
  const bw = cx.measureText("BEAT").width;
  cx.fillStyle = "#00f0ff";
  cx.fillText("FLOW", 60 + bw, 80);
  cx.fillStyle = "#ffffff";
  cx.font = "800 72px 'Barlow Condensed', sans-serif";
  const title = (tab.title || "Drum Tab").toUpperCase();
  cx.fillText(title.length > 26 ? `${title.slice(0, 25)}…` : title, 60, 170);
  cx.fillStyle = "#ffab00";
  cx.font = "600 34px 'DM Sans', sans-serif";
  cx.fillText(tab.artist || "", 60, 220);
  if (caption) {
    cx.fillStyle = "#e2e8f0";
    cx.font = "italic 500 28px 'DM Sans', sans-serif";
    cx.fillText(caption, 60, 262);
  }
  cx.fillStyle = "#94a3b8";
  cx.font = "500 26px 'JetBrains Mono', monospace";
  cx.fillText([tab.bpm ? `${tab.bpm} BPM` : "", tab.time_signature, tab.difficulty].filter(Boolean).join("  ·  "), 60, caption ? 306 : 266);
  const top = caption ? 336 : 300;
  const boxH = S - top - 120;
  cx.fillStyle = "#f7f4ec";
  cx.beginPath();
  cx.roundRect(40, top, S - 80, boxH, 24);
  cx.fill();
  let y = top + 16;
  for (const svg of svgs) {
    const w = svg.width.baseVal.value;
    const h = svg.height.baseVal.value;
    const dw = S - 120;
    const dh = (h * dw) / w;
    if (y + dh > top + boxH - 8) break;
    const im = await svgToImage(svg);
    cx.drawImage(im, 60, y, dw, dh);
    y += dh;
  }
  cx.fillStyle = "#e2e8f0";
  cx.font = "600 30px 'DM Sans', sans-serif";
  cx.fillText("Learn it with AI drum tabs →", 60, S - 52);
  cx.fillStyle = "#00f0ff";
  cx.font = "700 30px 'JetBrains Mono', monospace";
  const site = "beat.drumkitpro.com";
  cx.fillText(site, S - 60 - cx.measureText(site).width, S - 52);
  return new Promise((r) => c.toBlob(r, "image/png"));
}

export const ShareSocial = ({ tab, isPro, onUpgrade, createLink, captureSheet, onMakeClip }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const linkRef = useRef({ key: null, url: null });
  const key = `${tab.title}|${tab.artist}`;

  const getLink = async () => {
    if (linkRef.current.key !== key) linkRef.current = { key, url: await createLink() };
    return linkRef.current.url;
  };

  const shareTo = async ([, name, build]) => {
    if (!isPro) { onUpgrade(); return; }
    const w = window.open("", "_blank");
    try {
      const url = await getLink();
      const text = `Learning "${tab.title}"${tab.artist ? ` by ${tab.artist}` : ""} on the drums with BeatFlow`;
      const target = build(encodeURIComponent(url), encodeURIComponent(text));
      if (w) w.location.href = target; else window.location.href = target;
    } catch (e) {
      if (w) w.close();
      toast.error(`Could not share to ${name}`);
    }
  };

  const copyLink = async () => {
    if (!isPro) { onUpgrade(); return; }
    try {
      await navigator.clipboard.writeText(await getLink());
      toast.success("Public share link copied to clipboard!");
    } catch (e) {
      toast.error("Could not create share link");
    }
  };

  const downloadImage = async () => {
    setBusy(true);
    try {
      const svgs = await captureSheet();
      const blob = await buildImage(tab, svgs, readCaption(user).trim());
      const name = `${(tab.title || "drum-tab").replace(/[^a-z0-9]/gi, "_")}_beatflow.png`;
      const file = new File([blob], name, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] }) && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
        await navigator.share({ files: [file], title: tab.title });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        toast.success("Image saved — post it to Instagram or TikTok");
      }
    } catch (e) {
      if (e && e.name !== "AbortError") toast.error("Could not create image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy} className="h-9 border-white/15 bg-white/5 text-xs" data-testid="share-social-button">
          <Share2 className="w-4 h-4 mr-1.5" /> {busy ? "Creating…" : "Share"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#12161f] border-white/10 min-w-[210px]" data-testid="share-social-menu">
        <DropdownMenuLabel className="text-[10px] font-tab uppercase tracking-widest text-slate-500">Share to social</DropdownMenuLabel>
        {NETWORKS.map((n) => (
          <DropdownMenuItem key={n[0]} onClick={() => shareTo(n)} className="cursor-pointer focus:bg-white/5" data-testid={`share-${n[0]}`}>
            {n[1]} {!isPro && <Lock className="w-3 h-3 ml-auto text-[#ffab00]" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={copyLink} className="cursor-pointer focus:bg-white/5" data-testid="share-copy-link">
          <Link2 className="w-4 h-4 mr-2" /> Copy link {!isPro && <Lock className="w-3 h-3 ml-auto text-[#ffab00]" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={downloadImage} className="cursor-pointer focus:bg-white/5" data-testid="share-download-image">
          <ImageDown className="w-4 h-4 mr-2" /> Download image (Instagram / TikTok)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => (isPro ? onMakeClip() : onUpgrade())} className="cursor-pointer focus:bg-white/5" data-testid="share-make-clip">
          <Clapperboard className="w-4 h-4 mr-2" /> Make practice clip (video) {!isPro && <Lock className="w-3 h-3 ml-auto text-[#ffab00]" />}
        </DropdownMenuItem>
        {!isPro && <div className="px-2 py-1.5 text-[10px] text-slate-500">Links and clips are Pro features</div>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
